import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import { Link as RouterLink } from 'react-router-dom';
import {
  caseHighlights,
  companyProfile,
  deliveryPackages,
  homeMetrics,
  painPoints,
  serviceCards,
  workflowSteps
} from '../data/siteContent';

const asset = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

const serviceIcons = [<TipsAndUpdatesIcon />, <PrecisionManufacturingIcon />, <AutoGraphIcon />];

const HomePage = () => {
  return (
    <Stack spacing={5}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 5,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(128deg, #0b1f52 0%, #1e3a8a 42%, #35b5e5 100%)'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: { xs: -90, md: -40 },
            top: '50%',
            transform: 'translateY(-50%)',
            width: { xs: 220, md: 380 },
            height: { xs: 220, md: 380 },
            opacity: 0.18,
            pointerEvents: 'none',
            background: `url("${asset('/favicon.svg')}") center/contain no-repeat`
          }}
        />

        <Stack spacing={2.2} sx={{ maxWidth: 860, position: 'relative' }}>
          <Chip
            label={`${companyProfile.legalName} · 官网首批交付版`}
            sx={{ alignSelf: 'flex-start', color: 'white', bgcolor: 'rgba(255,255,255,0.12)' }}
          />
          <Typography variant="h2" sx={{ fontSize: { xs: 34, md: 52 }, fontWeight: 800, lineHeight: 1.12 }}>
            {companyProfile.brandName}
          </Typography>
          <Typography sx={{ fontSize: { xs: 16, md: 20 }, opacity: 0.96, lineHeight: 1.8 }}>
            {companyProfile.slogan}
          </Typography>
          <Typography sx={{ fontSize: { xs: 15, md: 17 }, opacity: 0.9, maxWidth: 820, lineHeight: 1.8 }}>
            当前版本重点补齐两件事：一是把官网内容从“泛介绍”升级为能支撑业务判断的真实内容；
            二是增加独立咨询入口，让访客可以直接生成需求摘要，进入下一轮沟通。
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} sx={{ pt: 1 }}>
            <Button
              component={RouterLink}
              to="/consultation"
              variant="contained"
              color="secondary"
              startIcon={<ChatRoundedIcon />}
              sx={{ px: 3, py: 1.2, fontWeight: 700 }}
            >
              进入咨询入口
            </Button>
            <Button
              component={RouterLink}
              to="/services"
              variant="outlined"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ px: 3, py: 1.2, color: 'white', borderColor: 'rgba(255,255,255,0.55)' }}
            >
              查看服务与交付
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2.2}>
        {homeMetrics.map((item) => (
          <Grid key={item.label} item xs={12} md={4}>
            <Paper sx={{ p: 2.5, borderRadius: 3, height: '100%', border: '1px solid #dbe7ff' }}>
              <Typography variant="overline" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 800 }}>
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.2}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #dbe7ff', height: '100%' }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
              当前官网补齐的关键内容
            </Typography>
            <Stack spacing={1.2}>
              {painPoints.map((item) => (
                <Box key={item} sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
                  <HubRoundedIcon color="primary" sx={{ mt: 0.2 }} />
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
              核心服务
            </Typography>
            <Grid container spacing={2.2}>
              {serviceCards.map((card, index) => (
                <Grid key={card.title} item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 2.6,
                      borderRadius: 3,
                      border: '1px solid #dbe7ff',
                      height: '100%',
                      transition: 'transform .2s ease, box-shadow .2s ease',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 14px 26px rgba(30,58,138,.10)' }
                    }}
                  >
                    <Box sx={{ mb: 1.2, color: 'primary.main' }}>{serviceIcons[index]}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {card.title}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.7, mb: 1.3, lineHeight: 1.7 }}>
                      {card.subtitle}
                    </Typography>
                    <Divider sx={{ my: 1.2 }} />
                    <Stack spacing={0.8}>
                      {card.points.map((point) => (
                        <Typography key={point} variant="body2" color="text.secondary">
                          • {point}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
          第一批可交付功能
        </Typography>
        <Grid container spacing={2.2}>
          {deliveryPackages.map((item) => (
            <Grid key={item.title} item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #dbe7ff', height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ChecklistRoundedIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {item.title}
                  </Typography>
                </Stack>
                <Typography sx={{ mt: 1, color: 'primary.main', fontWeight: 700 }}>{item.duration}</Typography>
                <Typography color="text.secondary" sx={{ mt: 1, mb: 1.5, lineHeight: 1.8 }}>
                  {item.summary}
                </Typography>
                <Stack spacing={0.9}>
                  {item.deliverables.map((deliverable) => (
                    <Typography key={deliverable} variant="body2" color="text.secondary">
                      • {deliverable}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
          典型案例与结果
        </Typography>
        <Grid container spacing={2.2}>
          {caseHighlights.map((item) => (
            <Grid key={item.title} item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #dbe7ff', height: '100%' }}>
                <Chip label={item.industry} size="small" color="primary" variant="outlined" />
                <Typography variant="h6" sx={{ mt: 1.2, fontWeight: 800 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ mt: 1, color: 'primary.main', fontWeight: 700, lineHeight: 1.7 }}>
                  {item.outcome}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.8 }}>
                  {item.details}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
          交付流程
        </Typography>
        <Grid container spacing={2.2}>
          {workflowSteps.map((item) => (
            <Grid key={item.title} item xs={12} md={3}>
              <Paper sx={{ p: 2.6, borderRadius: 3, border: '1px solid #dbe7ff', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {item.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.8 }}>
                  {item.description}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1.2, color: 'primary.main', fontWeight: 700 }}>
                  输出：{item.output}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          color: 'white',
          background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 55%, #2563eb 100%)'
        }}
      >
        <Grid container spacing={2.2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              现在就进入转化入口
            </Typography>
            <Typography sx={{ mt: 1.2, lineHeight: 1.8, opacity: 0.94, maxWidth: 860 }}>
              新增的咨询页支持填写项目背景、自动生成需求摘要，并能直接复制或下载，
              方便把“想做什么”和“首期交什么”快速沉淀下来。
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={1.2}>
              <Button component={RouterLink} to="/consultation" variant="contained" color="secondary">
                立即填写需求摘要
              </Button>
              <Button
                component={RouterLink}
                to="/api-web"
                variant="outlined"
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)' }}
              >
                打开 API Web
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );
};

export default HomePage;
